// src/features/users/components/UserCreate.tsx

import { useMemo, useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  PasswordInput,
  SelectInput,
  ReferenceArrayInput,
  SelectArrayInput,
  required,
  minLength,
  maxLength,
  email,
  useTranslate,
  useNotify,
  useRedirect,
  usePermissions,
} from 'react-admin';
import { Grid, Box, Alert, FormControlLabel, Switch, Backdrop, CircularProgress, Typography, alpha, useTheme } from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  GroupWork as GroupWorkIcon,
} from '@mui/icons-material';
import type { UserRole } from '@/types/models';
import { MyFormContainer } from '@/components/forms/MyFormContainer';
import { MyFormSection } from '@/components/forms/MyFormLayout';
import { MyToolbar } from '@/components/forms/MyToolbar';
import { AddGroupHotelsButton } from './AddGroupHotelsButton';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useWatch, useFormState } from 'react-hook-form';
import { getAssignableRoles, roleNeedsHotels } from '../roles';

/**
 * Validation rules for user creation
 */
const validateEmail = [
  required('resources.users.validation.email_required'),
  email('resources.users.validation.email_invalid'),
];

const validateFirstName = [
  required('resources.users.validation.firstName_required'),
  minLength(2, 'resources.users.validation.name_min_length'),
  maxLength(100, 'resources.users.validation.name_max_length'),
];

const validateLastName = [
  required('resources.users.validation.lastName_required'),
  minLength(2, 'resources.users.validation.name_min_length'),
  maxLength(100, 'resources.users.validation.name_max_length'),
];

const validatePassword = [
  required('resources.users.validation.password_required'),
  minLength(8, 'resources.users.validation.password_min_length'),
];

const validateRole = [
  required('resources.users.validation.role_required'),
];

/**
 * Form-level validation: ensures required assignments are present based on role
 */
const validateUserForm = (values: any) => {
  const errors: Record<string, string> = {};
  const role = values.role;

  if (roleNeedsHotels(role) &&
      (!values.assignedHotels || values.assignedHotels.length === 0)) {
    errors.assignedHotels = 'resources.users.validation.hotels_required_for_role';
  }

  if (role === 'GroupAdmin' &&
      (!values.assignedGroups || values.assignedGroups.length === 0)) {
    errors.assignedGroups = 'resources.users.validation.groups_required_for_role';
  }

  return errors;
};


/**
 * Form content with responsive grid layout.
 * Shows assignments side-by-side on desktop when role requires them.
 */
const CreateFormContent = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const { permissions } = usePermissions();
  const currentRole = permissions as UserRole | undefined;
  const { canManageSuperAdmins } = useUserPermissions();
  const role = useWatch({ name: 'role' });
  const [useExternalLogin, setUseExternalLogin] = useState(false);
  const { isSubmitting } = useFormState();

  const needsAssignments = role === 'GroupAdmin' || roleNeedsHotels(role);
  const canAssignGroups = role === 'GroupAdmin';
  const canAssignHotels = roleNeedsHotels(role);

  const assignableRoles = useMemo(() => getAssignableRoles(currentRole, canManageSuperAdmins), [currentRole, canManageSuperAdmins]);
  const roleChoices = useMemo(() =>
    assignableRoles.map((r) => ({
      id: r,
      name: translate(`resources.users.roles.${r.charAt(0).toLowerCase() + r.slice(1)}`),
    })),
    [assignableRoles, translate]
  );

  return (
    <MyFormContainer maxWidth="lg">
      {isSubmitting && (
        <Backdrop
          open
          sx={{
            position: 'absolute',
            zIndex: 1,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
          }}
        >
          <CircularProgress size={32} />
          <Typography sx={{ ml: 2, color: 'text.secondary' }}>
            {translate('resources.users.messages.saving_user')}
          </Typography>
        </Backdrop>
      )}
      <Box sx={{
        maxWidth: needsAssignments ? '1000px' : '600px',
        transition: 'max-width 0.3s ease',
        mx: 'auto',
        width: '100%',
        py: { xs: 2, md: 3 },
      }}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} md={needsAssignments ? 7 : 12}>
            <MyFormSection
              title={translate('resources.users.create.title')}
              subtitle={translate('resources.users.create.subtitle')}
              icon={<PersonAddIcon />}
              variant="outlined"
            >
              {/* First Name */}
              <TextInput
                source="firstName"
                label={translate('resources.users.fields.firstName')}
                validate={validateFirstName}
                fullWidth
              />

              {/* Last Name */}
              <TextInput
                source="lastName"
                label={translate('resources.users.fields.lastName')}
                validate={validateLastName}
                fullWidth
              />

              {/* Email */}
              <TextInput
                source="email"
                label={translate('resources.users.fields.email')}
                validate={validateEmail}
                fullWidth
                type="email"
              />

              {/* Role */}
              <SelectInput
                source="role"
                label={translate('resources.users.fields.role')}
                choices={roleChoices}
                validate={validateRole}
                fullWidth
                helperText={translate('resources.users.validation.role_helper')}
              />

              {/* External login toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={useExternalLogin}
                    onChange={(e) => setUseExternalLogin(e.target.checked)}
                  />
                }
                label={translate('resources.users.messages.use_external_login')}
                sx={{ mt: 1, mb: 1 }}
              />

              {useExternalLogin ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {translate('resources.users.messages.external_login_info')}
                </Alert>
              ) : (
                <>
                  {/* Temporary Password */}
                  <PasswordInput
                    source="temporaryPassword"
                    label={translate('resources.users.fields.temporaryPassword')}
                    validate={validatePassword}
                    fullWidth
                    helperText={translate('resources.users.validation.temporary_password_helper')}
                  />

                  <Alert severity="info" sx={{ mt: 1 }}>
                    {translate('resources.users.messages.temporary_password_info')}
                  </Alert>
                </>
              )}
            </MyFormSection>
          </Grid>

          {needsAssignments && (
            <Grid item xs={12} md={5}>
              <MyFormSection
                title={translate('resources.users.sections.assignments')}
                subtitle={translate('resources.users.sections.assignments_subtitle')}
                icon={<GroupWorkIcon />}
                variant="outlined"
              >
                {canAssignGroups && (
                  <ReferenceArrayInput
                    source="assignedGroups"
                    reference="groups"
                    sort={{ field: 'name', order: 'ASC' }}
                  >
                    <SelectArrayInput
                      label={translate('resources.users.fields.assignedGroups')}
                      optionText="name"
                      fullWidth
                      helperText={translate('resources.users.validation.groups_helper')}
                    />
                  </ReferenceArrayInput>
                )}

                {canAssignHotels && (
                  <>
                    <AddGroupHotelsButton />
                    <ReferenceArrayInput
                      source="assignedHotels"
                      reference="hotels"
                      sort={{ field: 'name', order: 'ASC' }}
                    >
                      <SelectArrayInput
                        label={translate('resources.users.fields.assignedHotels')}
                        optionText="name"
                        fullWidth
                        helperText={translate('resources.users.validation.hotels_helper')}
                      />
                    </ReferenceArrayInput>
                  </>
                )}
              </MyFormSection>
            </Grid>
          )}
        </Grid>
      </Box>
    </MyFormContainer>
  );
};

/**
 * User creation component
 * SuperAdmin, GroupAdmin, and HotelAdmin can create users
 */
export const UserCreate = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  const onSuccess = () => {
    notify('resources.users.messages.create_success', { type: 'success' });
    redirect('list', 'users');
  };

  const onError = (error: any) => {
    const errorCode = error?.body?.error?.code;
    let errorMessage = translate('resources.users.messages.create_error');

    // Handle specific error codes
    if (errorCode === 'EMAIL_ALREADY_EXISTS') {
      errorMessage = translate('resources.users.messages.email_already_exists');
    } else if (errorCode === 'MISSING_HOTEL_ASSIGNMENT') {
      errorMessage = translate('resources.users.validation.hotels_required_for_role');
    } else if (errorCode === 'MISSING_GROUP_ASSIGNMENT') {
      errorMessage = translate('resources.users.validation.groups_required_for_role');
    } else if (errorCode === 'INVALID_REQUEST') {
      errorMessage = error?.body?.error?.message || errorMessage;
    } else if (errorCode === 'VALIDATION_ERROR') {
      // Backend validation errors - show a generic translated message since field-specific messages would be in English
      errorMessage = translate('resources.users.messages.create_error');
    } else if (error?.body?.error?.message) {
      errorMessage = error.body.error.message;
    }

    notify(errorMessage, { type: 'error' });
  };

  return (
    <Create
      mutationOptions={{ onSuccess, onError }}
      redirect="list"
      title={translate('resources.users.create.title')}
    >
      <SimpleForm toolbar={<MyToolbar resource="users" />} validate={validateUserForm}>
        <CreateFormContent />
      </SimpleForm>
    </Create>
  );
};
